import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Button, Loading } from '@/components/ui/daisy';
import { ORDENABLE_PAGE_SIZE } from '@/constants';
import { useConfirm } from '@/data/context/ConfirmModalContext';
import { useTreeCourses } from '@/data/context/TreeCoursesContext';
import useCoursePathEvaluation from '@/data/hook/evaluation/useCoursePathEvaluation';
import useMasterEvaluations from '@/data/hook/evaluation/useMasterEvaluations';
import CoursePath from '@/models/CoursePath';
import Evaluation from '@/models/Evaluation';
import { CourseLineTabs } from '@/pages/course-line/CourseLineWorkspace';
import { mountKey } from '@/utils/courses';
import { ROUTES } from '@/utils/routes';
import { Link } from 'react-router-dom';
import { ArrowTopRightOnSquareIcon } from '../icons';
import CoursePathAssessmentFormService from './CoursePathAssessmentFormService';
import CoursePathEvaluationTemplateSelect from './CoursePathEvaluationTemplateSelect';
import EvaluationBlockCard from './EvaluationBlockCard';
import { EvaluationBlockInlineFormFields } from './EvaluationBlockInlineForm';
import { AssessmentEditState } from './EvaluationsTabContent';

type CoursePathEvaluationsTabContentProps = {
  coursePath: CoursePath;
  courseLineId: number;
};

export default function CoursePathEvaluationsTabContent({
  coursePath,
  courseLineId,
}: CoursePathEvaluationsTabContentProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.evaluationsTab',
  });
  const confirm = useConfirm();

  const { tree } = useTreeCourses();

  const [editingState, setEditingState] = useState<AssessmentEditState>(null);

  const { evaluations: templates, isLoading: isLoadingTemplates } =
    useMasterEvaluations({
      courseLineId,
      pageSize: ORDENABLE_PAGE_SIZE,
    });

  const path = tree?.current.findPath(
    mountKey({ type: 'course', id: coursePath.slug }),
  );

  const courseLineName = path?.at(1)?.name;
  const {
    evaluation,
    isLoading: isLoadingEvaluation,
    selectTemplate,
    updateEvaluation,
    isMutating,
    cacheManager,
  } = useCoursePathEvaluation({
    coursePathId: coursePath.id,
    courseLineId,
    masters: templates,
  });

  const handleUpdateBlock = async (
    target: Evaluation,
    data: EvaluationBlockInlineFormFields,
  ) => {
    await updateEvaluation({
      evaluationId: target.id,
      data: { name: data.name },
    });
  };

  const isLoading = isLoadingTemplates || isLoadingEvaluation;

  const handleSelectTemplate = (templateId: number) => {
    if (!evaluation) {
      selectTemplate(templateId);
      return;
    }
    confirm({
      title: t('coursePath.replaceConfirmTitle'),
      description: t('coursePath.replaceConfirmDescription'),
      variant: 'default',
      onConfirm: () => selectTemplate(templateId),
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loading variant="spinner" size="lg" color="primary" />
      </div>
    );
  }

  if (editingState) {
    return (
      <CoursePathAssessmentFormService
        evaluation={editingState.evaluation}
        coursePath={coursePath}
        cacheManager={cacheManager}
        assessment={editingState.assessment}
        onClose={() => setEditingState(null)}
      />
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <h2 className="text-lg font-semibold text-base-content">{t('title')}</h2>
      <CoursePathEvaluationTemplateSelect
        templates={templates}
        selectedTemplateId={evaluation?.sourceTemplateId}
        disabled={isMutating}
        onChange={handleSelectTemplate}
      />
      <ConditionalRenderer condition={templates.length === 0}>
        <span className="text-sm">
          {t('coursePath.noEvaluationsCreated', { courseLineName })}
        </span>
        <Link
          to={ROUTES.ADMIN.COURSE_LINES.INFO(courseLineId)}
          state={{ tab: CourseLineTabs.EVALUATIONS }}
        >
          <Button size="sm">
            {t('coursePath.editCourseLine')}
            <ArrowTopRightOnSquareIcon className="w-3 h-3" />
          </Button>
        </Link>
      </ConditionalRenderer>
      <ConditionalRenderer condition={!!evaluation}>
        {evaluation && (
          <EvaluationBlockCard
            evaluation={evaluation}
            cacheManager={cacheManager}
            onUpdate={handleUpdateBlock}
            isUpdating={isMutating}
            onAddAssessment={() => setEditingState({ evaluation })}
            onEditAssessment={assessment =>
              setEditingState({ evaluation, assessment })
            }
          />
        )}
      </ConditionalRenderer>
    </div>
  );
}
